<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <!-- Layout -->
        <SectionLayout :settings="fields.settings" :container-page="fields.containerPage" />

        <!-- Title Card -->
        <div class="mb-5">
          <label class="label-input">Title Section</label>
          <div class="group-item">
            <v-checkbox v-model="fields.titleCard.show" label="Show title block" hide-details class="mt-0 mb-2" />
            <ckeditor
              v-if="fields.titleCard.show"
              :editor="editor"
              v-model="fields.titleCard.content"
              :config="editorConfig"
            />
          </div>
        </div>

        <!-- Slide Items -->
        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Slide Items</label>
            <v-btn small color="primary" @click="handleAddSlide">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Slide</span>
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(slide, slideIdx) in fields.items" :key="`slide-${slideIdx}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span> Slide {{ slideIdx + 1 }} </span>
                    <v-btn icon @click.stop="handleRemoveSlide(slideIdx)">
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <!-- layout Item -->
                  <div class="sub-block">
                    <strong>Layout Item</strong>
                    <v-row class="mt-1">
                      <v-col cols="12" md="12">
                        <label>Gutter</label>
                        <v-text-field v-model.number="slide.gutter" hide-details class="mt-0" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-checkbox
                          v-model="slide.useCustomWidthMedia"
                          label="Use Custom Width Media"
                          hide-details
                          class="mt-0"
                        />
                        <div v-if="slide.useCustomWidthMedia" class="mt-1">
                          <label>Media Width</label>
                          <v-text-field
                            type="number"
                            suffix="px"
                            v-model.number="slide.mediaWidth"
                            hide-details
                            class="mt-0"
                          />
                        </div>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-checkbox v-model="slide.centerY" label="Center Y" hide-details class="mt-0" />
                      </v-col>
                    </v-row>
                  </div>
                  <!-- Media -->
                  <div class="sub-block">
                    <strong>Media</strong>
                    <v-row class="mt-1">
                      <v-col cols="12" md="6">
                        <div class="mb-2">
                          <label>Media Order</label>
                          <v-select
                            v-model.number="slide.media.order"
                            @change="value => changeMediaOrder(value, slideIdx)"
                            :items="[
                              { text: 'Left', value: 1 },
                              { text: 'Right', value: 2 },
                            ]"
                            hide-details
                            class="mt-0"
                          />
                        </div>

                        <div>
                          <v-radio-group small row v-model="slide.media.type" dense hide-details class="mt-0">
                            <v-radio small label="Image" value="image" />
                            <v-radio small label="Video" value="video" />
                          </v-radio-group>
                        </div>
                      </v-col>
                      <v-col cols="12" md="6">
                        <div v-if="slide.media.type === 'image'">
                          <UploadImage
                            :image="{ imageSrc: slide.media.image }"
                            :height="`260`"
                            :typeUpload="'image'"
                            @callback="obj => (slide.media.image = obj.imageSrc)"
                            @callbackDelete="slide.media.image = ''"
                          />
                        </div>
                        <div v-if="slide.media.type === 'video'">
                          <label>Video URL</label>
                          <div class="d-flex align-center gap-3">
                            <v-text-field v-model="slide.media.video" hide-details class="mt-0" />
                            <v-btn class="ml-2" color="primary" @click.stop="handleReloadVideo(slideIdx)">
                              <v-icon size="16">mdi-refresh</v-icon>
                            </v-btn>
                          </div>
                          <div class="mt-2">
                            <div>
                              <video
                                ref="videoPlayer"
                                style="width: 40%; height: auto;"
                                :key="`video-preview-${slideIdx}-${slide.media.video}`"
                                :src="slide.media.video"
                                autoplay
                                muted
                                loop
                                playsinline
                              ></video>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Content -->
                  <div class="sub-block">
                    <strong>Content</strong>

                    <!-- Center Y -->
                    <div class="sub-block">
                      <v-checkbox v-model="slide.centerY" label="Center Y" hide-details class="mt-0 mb-2" />
                    </div>

                    <!-- Title Description -->
                    <div class="sub-block">
                      <v-checkbox
                        v-model="slide.content.titleDescription.show"
                        label="Show Title & Description"
                        hide-details
                        class="mt-0 mb-2"
                      />
                      <ckeditor
                        v-if="slide.content.titleDescription.show"
                        :editor="editor"
                        v-model="slide.content.titleDescription.content"
                        :config="editorConfig"
                      />
                    </div>

                    <!-- Survey -->
                    <div class="sub-block">
                      <v-checkbox
                        v-model="slide.content.survey.show"
                        label="Show Survey"
                        hide-details
                        class="mt-0 mb-2"
                      />
                      <template v-if="slide.content.survey.show">
                        <v-row>
                          <v-col cols="12" md="6">
                            <label>Circle Color</label>
                            <div class="d-flex align-center gap-3">
                              <input
                                type="color"
                                style="width: 40px; height: 40px; margin-right: 12px"
                                v-model="slide.content.survey.colorPercent"
                              />
                              <v-text-field v-model="slide.content.survey.colorPercent" hide-details class="mt-0" />
                            </div>
                          </v-col>
                          <v-col cols="12">
                            <label>Survey Title</label>
                            <ckeditor :editor="editor" v-model="slide.content.survey.title" :config="editorConfig" />
                          </v-col>
                        </v-row>
                        <div class="d-flex justify-end mt-2 mb-2">
                          <v-btn small color="primary" @click="handleAddSurveyItem(slideIdx)">
                            <v-icon size="16">mdi-plus</v-icon>
                            <span>Add Survey Item</span>
                          </v-btn>
                        </div>
                        <v-expansion-panels accordion>
                          <v-expansion-panel
                            v-for="(item, si) in slide.content.survey.items"
                            :key="`survey-${slideIdx}-${si}`"
                          >
                            <v-expansion-panel-header>
                              <div class="d-flex justify-space-between align-center" style="width: 100%">
                                <span>Item {{ si + 1 }} - {{ item.percent }}%</span>
                                <v-btn icon @click.stop="handleRemoveSurveyItem(slideIdx, si)">
                                  <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                                </v-btn>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="mb-3">
                                <label>Percent (0 - 100)</label>
                                <v-text-field
                                  v-model.number="item.percent"
                                  type="number"
                                  min="0"
                                  max="100"
                                  hide-details
                                  class="mt-0"
                                />
                              </div>
                              <div>
                                <label>Description</label>
                                <ckeditor :editor="editor" v-model="item.description" :config="editorConfig" />
                              </div>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </template>
                    </div>

                    <!-- Accordion -->
                    <div class="sub-block">
                      <v-checkbox
                        v-model="slide.content.accordion.show"
                        label="Show Accordion"
                        hide-details
                        class="mt-0 mb-2"
                      />
                      <template v-if="slide.content.accordion.show">
                        <div class="d-flex justify-end mt-2 mb-2">
                          <v-btn small color="primary" @click="handleAddAccordion(slideIdx)">
                            <v-icon size="16">mdi-plus</v-icon>
                            <span>Add Accordion</span>
                          </v-btn>
                        </div>
                        <v-expansion-panels accordion>
                          <v-expansion-panel
                            v-for="(item, ai) in slide.content.accordion.items"
                            :key="`accord-${slideIdx}-${ai}`"
                          >
                            <v-expansion-panel-header>
                              <div class="d-flex justify-space-between align-center" style="width: 100%">
                                <span>{{ stripHtml(item.title) || `Accordion ${ai + 1}` }}</span>
                                <v-btn icon @click.stop="handleRemoveAccordion(slideIdx, ai)">
                                  <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                                </v-btn>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="mb-3">
                                <label>Title</label>
                                <v-text-field v-model="item.title" hide-details class="mt-0" />
                              </div>
                              <div>
                                <label>Content</label>
                                <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                              </div>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </template>
                    </div>

                    <!-- Items with Icon -->
                    <div class="sub-block">
                      <v-checkbox
                        v-model="slide.content.itemsWithIcon.show"
                        label="Show Items With Icon"
                        hide-details
                        class="mt-0 mb-2"
                      />
                      <template v-if="slide.content.itemsWithIcon.show">
                        <div class="d-flex justify-end mt-2 mb-2">
                          <v-btn small color="primary" @click="handleAddIconItem(slideIdx)">
                            <v-icon size="16">mdi-plus</v-icon>
                            <span>Add Item</span>
                          </v-btn>
                        </div>
                        <v-expansion-panels accordion>
                          <v-expansion-panel
                            v-for="(item, ii) in slide.content.itemsWithIcon.items"
                            :key="`icon-${slideIdx}-${ii}`"
                          >
                            <v-expansion-panel-header>
                              <div class="d-flex justify-space-between align-center" style="width: 100%">
                                <span>{{ stripHtml(item.title) || `Item ${ii + 1}` }}</span>
                                <v-btn icon @click.stop="handleRemoveIconItem(slideIdx, ii)">
                                  <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                                </v-btn>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="mb-3">
                                <label>Icon</label>
                                <UploadImage
                                  :image="{ imageSrc: item.icon }"
                                  :height="`80`"
                                  :typeUpload="'image'"
                                  @callback="obj => (item.icon = obj.imageSrc)"
                                  @callbackDelete="item.icon = ''"
                                />
                              </div>
                              <div class="mb-3">
                                <label>Title</label>
                                <v-text-field v-model="item.title" hide-details class="mt-0" />
                              </div>
                              <div>
                                <label>Description</label>
                                <v-textarea v-model="item.description" rows="2" hide-details class="mt-0" />
                              </div>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </template>
                    </div>

                    <!-- Btn Dynamic (inside content) -->
                    <div class="sub-block">
                      <label class="d-block mb-2">Button Block (inside content)</label>
                      <ButtonPrimary :button="slide.content.button" />
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <!-- Btn Primary (out swiper) -->
        <div class="mb-5">
          <label class="label-input">Primary Button (out content)</label>
          <div class="group-item">
            <ButtonPrimary :button="fields.buttonOutSide" />
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import ButtonPrimary from '../common/ui-modules/button-primary/editor.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';

const buildButtonAreaDefault = () => ({
  align: 'flex-start',
  backgroundColor: '#606F99',
  color: '#FFFFFF',
  radius: 4,
  iconPrefix: {
    show: false,
    src: '',
  },
  iconSuffix: {
    show: true,
    src: 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/03/image-6a1f8e0dd4865b45728c40d9.png',
  },
  text: 'Shop Now',
  link: '#',
  show: true,
  subBtn: {
    show: false,
    text: '30-Day Money-Back Guararantee',
  },
  policy: {
    show: false,
    colPerView: 2,
    alignContent: 'center',
    items: [],
  },
});

const buildSlideDefault = () => ({
  centerY: false,
  gutter: 30,
  mediaWidth: 50,
  useCustomWidthMedia: false,
  media: {
    type: 'image',
    order: 1,
    image: '',
    video: '',
  },
  content: {
    order: 2,
    titleDescription: {
      show: true,
      content:
        '<h2>Dermatologist-Backed Comfort You Can Trust</h2><p>Lorem ipsum dolor sit amet consectetur adipisicing elit.</p>',
    },
    survey: {
      show: true,
      colorPercent: '#606F99',
      title: '<h3>Our Boxers are Loved by Parents</h3>',
      items: [{ percent: 91, description: 'shared that our robot helped their children build patience and focus.' }],
    },
    accordion: {
      show: true,
      items: [
        { title: 'Post-Surgery (e.g., Prostate)', content: 'Perfect for recovery after prostate or bladder surgery.' },
      ],
    },
    itemsWithIcon: {
      show: true,
      items: [
        { icon: '', title: 'Quick Dispatch, anywhere in the world', description: 'Made with sustainable bamboo.' },
      ],
    },
    button: buildButtonAreaDefault(),
  },
});

const getDefaultFields = () => ({
  title: 'Card Multi Boxer Section',
  settings: {
    paddingTop: true,
    paddingBottom: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: false,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  titleCard: {
    show: true,
    content:
      '<h2>Dermatologist-Backed Comfort You Can Trust</h2><p>Lorem ipsum dolor sit amet consectetur adipisicing elit.</p>',
  },
  items: [buildSlideDefault()],
  buttonOutSide: buildButtonAreaDefault(),
});

export default {
  name: 'CardMultiBoxerSectionEditor',
  components: { UploadImage, ButtonPrimary, SectionLayout },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
      mediaTypeOptions: [
        { text: 'Image', value: 'image' },
        { text: 'Video', value: 'video' },
      ],
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
    };
  },
  methods: {
    handleReloadVideo() {
      this.$nextTick(() => {
        const players = this.$refs.videoPlayer;
        const list = Array.isArray(players) ? players : players ? [players] : [];
        list.forEach(video => {
          if (video && typeof video.load === 'function') video.load();
        });
      });
    },
    changeMediaOrder(value, slideIdx) {
      const item = this.fields.items[slideIdx];
      if (!item) return;
      item.media.order = value;
      item.content.order = value === 1 ? 2 : 1;
    },
    handleChangeButtonOutSide(button) {
      this.$set(this.fields, 'buttonOutSide', button);
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCardMultiBoxer(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    stripHtml(html) {
      if (!html) return '';
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return (tmp.textContent || tmp.innerText || '').trim();
    },
    handleAddSlide() {
      this.fields.items.push(cloneDeep(buildSlideDefault()));
    },
    handleRemoveSlide(index) {
      if (this.fields.items.length <= 1) return;
      this.fields.items.splice(index, 1);
    },
    handleAddSurveyItem(slideIdx) {
      this.fields.items[slideIdx].content.survey.items.push({ percent: 80, description: '' });
    },
    handleRemoveSurveyItem(slideIdx, index) {
      const arr = this.fields.items[slideIdx].content.survey.items;
      if (arr.length <= 1) return;
      arr.splice(index, 1);
    },
    handleAddAccordion(slideIdx) {
      this.fields.items[slideIdx].content.accordion.items.push({ title: '', content: '' });
    },
    handleRemoveAccordion(slideIdx, index) {
      const arr = this.fields.items[slideIdx].content.accordion.items;
      if (arr.length <= 1) return;
      arr.splice(index, 1);
    },
    handleAddIconItem(slideIdx) {
      this.fields.items[slideIdx].content.itemsWithIcon.items.push({ icon: '', title: '', description: '' });
    },
    handleRemoveIconItem(slideIdx, index) {
      const arr = this.fields.items[slideIdx].content.itemsWithIcon.items;
      if (arr.length <= 1) return;
      arr.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.sub-block {
  border-top: 1px dashed #0000001a;
  padding-top: 12px;
  margin-top: 12px;
}
.ck-editor__editable_inline,
.ck-editor__editable {
  min-height: 140px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
